import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { APP_ROUTES } from '../routes.js';
import { createDashboardUrl, getDashboardUrls } from '../api/index.js';
import AddDashboardUrlModal from '../components/AddDashboardUrlModal.jsx';
import Spinner from '../components/Spinner.jsx';
import Toast from '../components/Toast.jsx';
import { useToast } from '../hooks/useToast.js';
import pageStyles from './Page.module.css';
import styles from './HomePage.module.css';

const CUSTOM_URL_ICON = '🔗';

export default function HomePage() {
  const [urls, setUrls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [search, setSearch] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [toast, showToast] = useToast();

  const loadUrls = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    try {
      const data = await getDashboardUrls();
      setUrls(data);
    } catch (e) {
      setFetchError(e.message || 'Failed to load custom URLs');
      setUrls([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUrls();
  }, [loadUrls]);

  const filteredUrls = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return urls;
    return urls.filter((item) =>
      String(item.title ?? '').toLowerCase().includes(query)
    );
  }, [urls, search]);

  const handleCreate = async (payload) => {
    const { data, message } = await createDashboardUrl(payload);
    setShowAddModal(false);
    showToast(message || 'URL added successfully');
    if (data?.id != null) {
      setUrls((prev) =>
        [...prev, data].sort((a, b) =>
          String(a.title ?? '').localeCompare(String(b.title ?? ''))
        )
      );
    } else {
      await loadUrls();
    }
  };

  const handleCopyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      showToast('URL copied to clipboard');
    } catch {
      showToast('Could not copy URL', 'error');
    }
  };

  return (
    <div className={pageStyles.page}>
      <div className={pageStyles.pageHeader}>
        <h1 className={pageStyles.pageTitle}>Admin Dashboard</h1>
        <p className={pageStyles.pageSubtitle}>
          Choose a section below to get started.
        </p>
      </div>

      <ul className={styles.routeList}>
        {APP_ROUTES.map(({ path, name, description, icon }) => (
          <li key={path} className={styles.routeItem}>
            <Link to={path} className={styles.routeLink}>
              <span className={styles.routeIcon} aria-hidden="true">
                {icon}
              </span>
              <span className={styles.routeContent}>
                <span className={styles.routeName}>{name}</span>
                <span className={styles.routePath}>{path}</span>
                <span className={styles.routeDescription}>{description}</span>
              </span>
              <span className={styles.routeArrow} aria-hidden="true">
                →
              </span>
            </Link>
          </li>
        ))}
      </ul>

      <section className={styles.customUrlsSection}>
        <div className={styles.customUrlsHeader}>
          <div>
            <h2 className={styles.customUrlsTitle}>Custom URLs</h2>
            <p className={styles.customUrlsSubtitle}>
              Quick links to external dashboards and tools.
            </p>
          </div>
          <button
            type="button"
            className={styles.addUrlBtn}
            onClick={() => setShowAddModal(true)}
          >
            ＋ Add URL
          </button>
        </div>

        {!loading && urls.length > 0 && (
          <div className={styles.searchWrap}>
            <input
              type="search"
              className={styles.searchInput}
              placeholder="Filter by title…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Filter custom URLs by title"
            />
          </div>
        )}

        {loading && <Spinner />}

        {!loading && fetchError && (
          <div className={styles.errorBanner}>⚠ {fetchError}</div>
        )}

        {!loading && !fetchError && urls.length === 0 && (
          <div className={styles.emptyState}>
            <span className={styles.emptyIcon} aria-hidden="true">
              🔗
            </span>
            <p className={styles.emptyMsg}>No custom URLs yet</p>
            <p className={styles.emptyHint}>
              Add your first dashboard link with the button above.
            </p>
          </div>
        )}

        {!loading && !fetchError && urls.length > 0 && filteredUrls.length === 0 && (
          <div className={styles.emptyState}>
            <p className={styles.emptyMsg}>No URLs match &ldquo;{search}&rdquo;</p>
          </div>
        )}

        {!loading && !fetchError && filteredUrls.length > 0 && (
          <ul className={styles.urlList}>
            {filteredUrls.map((item) => (
              <li key={item.id ?? item.url} className={styles.urlItem}>
                <span className={styles.urlIcon} aria-hidden="true">
                  {CUSTOM_URL_ICON}
                </span>
                <div className={styles.urlMain}>
                  <h3 className={styles.urlTitle}>{item.title}</h3>
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.urlLink}
                  >
                    {item.url}
                  </a>
                  {item.description && (
                    <p className={styles.urlDescription}>{item.description}</p>
                  )}
                </div>
                <button
                  type="button"
                  className={styles.copyBtn}
                  onClick={() => handleCopyUrl(item.url)}
                  title="Copy URL"
                >
                  Copy
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {showAddModal && (
        <AddDashboardUrlModal
          onClose={() => setShowAddModal(false)}
          onSave={handleCreate}
        />
      )}

      <Toast visible={toast.visible} msg={toast.msg} type={toast.type} />
    </div>
  );
}
